// ✅ src/pages/VehicleV2/VehicleFuelType.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/fuelType", fuelType);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/fuelType/view?id=..
//   POST https://nissi-india.com/api/fuelType/save
//
// Notes:
// - Uses common localStorage header pattern
// - Sends x-finyearid / x-finyear / x-branchid / x-branch / x-userid
// - Edit mode reads ?fuelTypeId=123
// - UI style kept similar to your other VehicleV2 pages

import HeaderBar from "../../components/HeaderBar";
import { useEffect, useMemo, useState, useContext, useCallback } from "react";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/fuelType";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const VehicleFuelType = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ edit id from query
  const fuelTypeId = useMemo(
    () => toInt(searchParams.get("fuelTypeId")),
    [searchParams]
  );

  // ✅ common localStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch =
    localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ rights / branch active
  const canSubmit = useMemo(() => {
    const v = sessionStorage.getItem("fueltype_addrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v =
      sessionStorage.getItem("BranchActive") ||
      localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // form
  const [fuelTypeName, setFuelTypeName] = useState("");

  /* ------------------------- LOAD VIEW ------------------------- */
  const fetchView = useCallback(async () => {
    if (!fuelTypeId) return;

    try {
      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { id: fuelTypeId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load fuel type");
      }

      const row = data?.data || data?.row || {};

      setFuelTypeName(
        row?.fld_fueltypename ||
          row?.fuelTypeName ||
          row?.fld_fueltype ||
          ""
      );
    } catch (err) {
      console.error("fuelType /view error:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load fuel type");
    }
  }, [fuelTypeId, commonHeaders]);

  useEffect(() => {
    (async () => {
      try {
        setPageLoading(true);
        await fetchView();
      } finally {
        setPageLoading(false);
      }
    })();
  }, [fetchView]);

  /* ------------------------- ACTIONS ------------------------- */
  const onClear = () => {
    setFuelTypeName("");
  };

  const onSave = async () => {
    const name = toStr(fuelTypeName);

    if (!name) {
      toast.error("Please enter Fuel Type");
      return;
    }

    if (!branchActive || !canSubmit) {
      toast.warn("Save rights not available.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        fuelTypeId: fuelTypeId || 0,
        fuelTypeName: name,
      };

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(data?.message || "Saved successfully");

      navigate("/vehicle/VehicleFuelTypeListing");
    } catch (err) {
      console.error("fuelType /save error:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE FUEL TYPE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {fuelTypeId ? "Edit Vehicle Fuel Type" : "Add Vehicle Fuel Type"}
              </h1>
            </div>

            <button
              type="button"
              onClick={() => navigate("/vehicle/VehicleFuelTypeListing")}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Listing
            </button>
          </div>

          {/* Body */}
          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            {pageLoading ? (
              <div className="py-10 text-center text-sm opacity-80">Loading...</div>
            ) : (
              <div className="max-w-2xl mx-auto">
                {/* Fuel Type */}
                <div className="mb-8">
                  <label
                    htmlFor="fuelTypeName"
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Fuel Type
                  </label>

                  <input
                    id="fuelTypeName"
                    type="text"
                    value={fuelTypeName}
                    onChange={(e) => setFuelTypeName(e.target.value)}
                    placeholder="Enter fuel type..."
                    className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={onClear}
                    className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={onSave}
                    disabled={loading || !toStr(fuelTypeName) || !canSubmit || !branchActive}
                    className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading || !toStr(fuelTypeName) || !canSubmit || !branchActive
                        ? "bg-gray-400 cursor-not-allowed"
                        : `${buttonClass} ${hoverClass}`
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="w-4 h-4" />
                        Save
                      </>
                    )}
                  </button>
                </div>

                {(!canSubmit || !branchActive) && (
                  <div className="mt-4 text-sm text-red-500">
                    Save disabled (no add rights or branch inactive).
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE FUEL TYPE
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleFuelType;